import { useEffect, useRef, useState } from 'react';
import type { Position } from '@shared/types';
import { PersonCard, levelLabel } from '../pages/PersonCard.js';
import { retireMark } from './OrgFlow.js';
import { CandidateCompare } from './CandidateCompare.js';
import { VacancyCandidates } from './VacancyCandidates.js';

/**
 * หน้าต่างรายละเอียดของหนึ่งอัตรา — เปิดจากรายการตำแหน่งในแผงข้าง
 *
 * ────────────────────────────────────────────────────────────────
 *  แบ่งเป็นสองชั้นโดยตั้งใจ
 * ────────────────────────────────────────────────────────────────
 *
 *   ชั้นบน  เรื่องของ "อัตรา" — เลขที่ สายงาน ระดับ ประเภท หน่วยงาน สถานะ
 *           ข้อมูลชุดนี้ติดมากับผังอยู่แล้ว จึงขึ้นทันทีที่เปิด ไม่ต้องรอโหลด
 *   ชั้นล่าง เรื่องของ "คน" — การ์ดประวัติเต็มของผู้ครองอัตรา
 *           โหลดจาก /api/people/:id/profile เมื่อเปิดหน้าต่างเท่านั้น
 *
 * ที่ต้องแยกเพราะสองอย่างนี้ไม่ได้มาคู่กันเสมอ อัตราที่ว่างไม่มีคน
 * และคนที่ไม่มีรหัสบุคคลในข้อมูลก็เชื่อกับประวัติไม่ได้ — ทั้งสองกรณีชั้นบนยังมีของให้ดู
 *
 * ⚠ ประวัติใช้ <PersonCard embedded /> ตัวเดิม ไม่ได้เขียนใหม่
 * ดูเหตุผลที่หัวคอมเมนต์ของ pages/PersonCard.tsx
 */
export function PositionModal({
  position,
  unitName,
  onClose,
}: {
  position: Position;
  unitName: string;
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const closeBtn = useRef<HTMLButtonElement | null>(null);
  /* สลับเนื้อในหน้าต่างเดิมเมื่อกดเทียบ ไม่ใช่เปิดหน้าต่างซ้อน — เหตุผลเดียวกับ PositionDetail */
  const [compare, setCompare] = useState<string[] | null>(null);

  /**
   * ปิดด้วย Esc · ล็อกไม่ให้หน้าข้างหลังเลื่อน · ส่งโฟกัสกลับที่เดิมตอนปิด
   *
   * ⚠ ต้องจำว่าโฟกัสอยู่ที่ไหนก่อนเปิด แล้วคืนให้ตอนปิด
   * ไม่งั้นคนที่ไล่ด้วยแป้น Tab จะถูกโยนกลับไปต้นหน้าทุกครั้งที่ปิดหน้าต่าง
   * แล้วต้องกด Tab ใหม่ยี่สิบกว่าครั้งเพื่อกลับมาที่รายการเดิม
   */
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      returnTo?.focus?.();
    };
  }, [onClose]);

  const p = position;
  const who = p.incumbent;

  return (
    <div
      className="pmodal-back"
      // กดพื้นหลังเพื่อปิด แต่ต้องเช็คว่ากดที่พื้นหลังจริง ไม่ใช่กดในกล่องแล้วลากออกมาปล่อย
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="pmodal"
        role="dialog"
        aria-modal="true"
        aria-label={`รายละเอียดอัตรา ${p.title}`}
        ref={box}
      >
        <header className="pmodal-hd">
          <div>
            <span className="kind">
              {p.vacant ? 'อัตราว่าง' : 'อัตราที่มีผู้ครอง'}
              {p.posNo && ` · เลขที่ ${p.posNo}`}
            </span>
            <b>{p.title}</b>
            <span className="sub">
              {levelLabel(p.level)} · {unitName}
            </span>
          </div>
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง" ref={closeBtn}>
            ✕
          </button>
        </header>

        <div className="pmodal-body">
          {compare ? (
            <CandidateCompare pos={p} ids={compare} onBack={() => setCompare(null)} />
          ) : (
          <>
          <section className="pmodal-sec">
            <h4>ข้อมูลอัตรา</h4>
            <dl className="pmodal-dl">
              <Row k="เลขที่ตำแหน่ง" v={p.posNo} />
              <Row k="ชื่อตำแหน่ง" v={p.title} />
              <Row k="สายงาน" v={p.line} />
              <Row k="ระดับ" v={p.level ? levelLabel(p.level) : null} />
              <Row k="ประเภทบุคลากร" v={CATEGORY_TH[p.category] ?? p.category} />
              <Row k="สังกัด" v={p.orgPath.length > 0 ? p.orgPath.join(' › ') : p.unit} />
              <Row k="สถานะ" v={p.vacant ? 'ว่าง' : 'มีผู้ครอง'} tone={p.vacant ? 'alert' : undefined} />
              {p.vacant && <Row k="ว่างตั้งแต่" v={thaiDate(p.vacantSince)} />}
              {p.vacant && (
                <Row k="ว่างมาแล้ว" v={p.vacantMonths === null ? null : duration(p.vacantMonths)} tone="alert" />
              )}
            </dl>

            {p.vacant && (
              <p className="pmodal-note">
                {p.lastHolder
                  ? `ผู้ครองคนก่อนหน้า: ${p.lastHolder.name}` +
                    /* ⚠ reason เป็น null แปลว่า "ไฟล์ต้นทางไม่มีข้อมูลนี้" ไม่ใช่ "ไม่ทราบสาเหตุ"
                       ต้องเขียนให้ต่างกัน เพราะสองอย่างนี้ต้องไปตามข้อมูลคนละทาง */
                    (p.lastHolder.reason
                      ? ` · พ้นจากตำแหน่งเพราะ${p.lastHolder.reason}`
                      : ' · ไฟล์ต้นทางไม่มีข้อมูลเหตุที่พ้นจากตำแหน่ง')
                  : 'ยังไม่ทราบผู้ครองคนก่อนหน้า — ข้อมูลชุดที่ได้จาก DPIS ไม่มีฟิลด์นี้มาด้วย'}
              </p>
            )}
          </section>

          {/* อัตราว่างต้องตอบคำถามถัดไปทันทีว่า "แล้วใครมาลงได้บ้าง"
              ไม่ใช่จบแค่บอกว่าว่างมากี่เดือน ซึ่งเป็นข้อมูลที่ใช้ทำอะไรต่อไม่ได้เลย */}
          {p.vacant && <VacancyCandidates pos={p} onCompare={setCompare} />}

          {who && (
            <section className="pmodal-sec">
              <h4>ผู้ครองอัตรา</h4>
              <dl className="pmodal-dl">
                <Row k="ชื่อ-สกุล" v={who.name} />
                <Row k="อายุ" v={who.age === null ? null : `${who.age} ปี`} />
                <Row k="วันเข้ารับราชการ" v={thaiDate(who.entryDate)} />
                <Row k="วันดำรงตำแหน่งนี้" v={thaiDate(who.occupyDate)} />
                <Row k="วันเกษียณ" v={thaiDate(who.retireDate)} />
                <Row k="ปีงบประมาณที่เกษียณ" v={who.retireFiscalYear === null ? null : String(who.retireFiscalYear)} />
                <Row
                  k="เหลืออีก"
                  v={who.retireInYears === null ? null : `${who.retireInYears.toFixed(1)} ปี`}
                  /* ใช้เกณฑ์ร่วมจาก OrgFlow ไม่เขียนเลขเอง — ดูคำเตือนที่ RETIRE_SOON_YEARS */
                  tone={retireMark(who)?.tone === 'soon' ? 'warn' : undefined}
                />
                <Row
                  k="ผ่านเกณฑ์เลื่อนระดับเบื้องต้น"
                  v={who.promotionEligible ? 'ผ่าน' : 'ยังไม่ผ่าน'}
                />
              </dl>
            </section>
          )}

          {who?.personaId ? (
            <section className="pmodal-sec is-profile">
              <h4>ประวัติรายบุคคล</h4>
              <PersonCard personaId={who.personaId} embedded />
            </section>
          ) : (
            who && (
              <p className="pmodal-note">
                {/* อัตราที่มีผู้ครองแต่ไม่มีรหัสบุคคล เชื่อกับตารางประวัติไม่ได้
                    เป็นปัญหาคุณภาพข้อมูลที่ต้องเห็น ไม่ใช่ซ่อน */}
                อัตรานี้มีผู้ครองแต่ข้อมูลไม่มีรหัสบุคคล จึงเชื่อมกับประวัติการดำรงตำแหน่งไม่ได้
              </p>
            )
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- ตัวช่วย ---------------- */

const CATEGORY_TH: Record<string, string> = {
  civil: 'ข้าราชการพลเรือนสามัญ',
  permanentEmployee: 'ลูกจ้างประจำ',
  temporaryEmployee: 'ลูกจ้างชั่วคราว',
  governmentEmployee: 'พนักงานราชการ',
  other: 'จัดประเภทไม่ได้',
};

/** แถวเดียวของตารางคู่ค่า — ค่าที่ไม่มีขึ้นขีดกลาง ไม่ใช่ซ่อนแถวทิ้ง
    เพราะ "ไม่มีข้อมูล" กับ "ไม่มีหัวข้อนี้" เป็นคนละเรื่องสำหรับคนที่กำลังตรวจข้อมูล */
function Row({ k, v, tone }: { k: string; v: string | null | undefined; tone?: 'alert' | 'warn' }) {
  return (
    <div>
      <dt>{k}</dt>
      <dd className={tone ? `is-${tone}` : undefined}>{v && v.trim() ? v : '—'}</dd>
    </div>
  );
}

const TH_MONTH = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];

function thaiDate(iso: string | null): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${Number(d)} ${TH_MONTH[Number(m) - 1]} ${Number(y) + 543}`;
}

function duration(months: number): string {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (!y) return `${m} เดือน`;
  return m ? `${y} ปี ${m} เดือน` : `${y} ปี`;
}
